import React, { useState } from "react";
import { Modal, Button, Badge, Row, Col, Form, Spinner } from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../../api";
import usePermission from "../../../../hooks/usePermission";

const formatMoney = (val) => {
  const num = Number(val || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

const getFileUrl = (filePath) => {
  if (!filePath) return "";
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) return filePath;
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
  const clean = filePath.replace(/^\/+/, "").replace(/^public\//, "");
  return `${baseUrl}/${clean}`;
};

const statusVariant = {
  PENDING: "warning",
  APPROVED: "info",
  SETTLED: "success",
  REJECTED: "danger",
  DRAFT: "secondary",
};

const ReimbursementDetailModal = ({
  show,
  onHide,
  claim,
  canReview,
  canSettle,
  onSuccess,
}) => {
  const { hasPermission, isAdmin } = usePermission();
  const canApprove = Boolean(isAdmin || hasPermission("approve:reimbursements"));
  const canReject = Boolean(isAdmin || hasPermission("reject:reimbursements"));
  const isReviewer = Boolean(
    canReview ?? (canApprove || canReject)
  );
  const isSettler = Boolean(
    canSettle ?? (isAdmin || hasPermission("settle:reimbursements"))
  );

  const [approvedAmount, setApprovedAmount] = useState("");
  const [reviewRemarks, setReviewRemarks] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectBox, setShowRejectBox] = useState(false);

  const [paymentMode, setPaymentMode] = useState("UPI");
  const [paymentRef, setPaymentRef] = useState("");
  const [settlementRemarks, setSettlementRemarks] = useState("");

  const [previewImage, setPreviewImage] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Initialize values when claim opens
  React.useEffect(() => {
    if (claim) {
      setApprovedAmount(claim.approvedAmount !== null ? claim.approvedAmount : claim.amount);
      setReviewRemarks(claim.approvalInfo?.remarks || "");
      setRejectReason("");
      setShowRejectBox(false);
      setPaymentMode(claim.settlementInfo?.paymentMode || "UPI");
      setPaymentRef(claim.settlementInfo?.paymentReference || "");
      setSettlementRemarks(claim.settlementInfo?.remarks || "");
    }
  }, [claim]);

  if (!claim) return null;

  const handleApprove = async () => {
    try {
      setProcessing(true);
      const res = await api.patch(`/employee-reimbursements/${claim._id}/approve`, {
        approvedAmount: Number(approvedAmount || claim.amount),
        remarks: reviewRemarks,
      });

      if (res.data.success) {
        swal("Success", res.data.message || "Claim approved successfully", "success");
        onSuccess && onSuccess();
        onHide();
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || err.message || "Failed to approve", "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      swal("Warning", "Please provide a rejection reason", "warning");
      return;
    }

    try {
      setProcessing(true);
      const res = await api.patch(`/employee-reimbursements/${claim._id}/reject`, {
        reason: rejectReason.trim(),
      });

      if (res.data.success) {
        swal("Success", "Claim has been rejected", "info");
        onSuccess && onSuccess();
        onHide();
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || err.message || "Failed to reject", "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleSettle = async () => {
    try {
      setProcessing(true);
      const res = await api.patch(`/employee-reimbursements/${claim._id}/settle`, {
        paymentMode,
        paymentReference: paymentRef.trim(),
        remarks: settlementRemarks.trim(),
      });

      if (res.data.success) {
        swal("Success", "Claim settled and marked as paid!", "success");
        onSuccess && onSuccess();
        onHide();
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || err.message || "Failed to settle", "error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <div className="d-flex align-items-center gap-2">
            <Modal.Title style={{ fontSize: 17, fontWeight: 700 }}>
              Expense Claim — {claim.claimId || "Claim Details"}
            </Modal.Title>
            <Badge bg={statusVariant[claim.status] || "secondary"} className="ms-2">
              {claim.status}
            </Badge>
          </div>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }}>
          {/* Staff & Basic Info Strip */}
          <div className="p-3 bg-light rounded border mb-3">
            <Row className="g-2">
              <Col sm={6} md={3}>
                <div className="text-muted small">STAFF NAME</div>
                <strong className="text-dark">{claim.employee?.name || claim.user?.name || "Staff"}</strong>
                <div className="text-muted" style={{ fontSize: 11 }}>{claim.employee?.level || "Employee"}</div>
              </Col>
              <Col sm={6} md={3}>
                <div className="text-muted small">EXPENSE DATE</div>
                <strong>{new Date(claim.expenseDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</strong>
              </Col>
              <Col sm={6} md={3}>
                <div className="text-muted small">CATEGORY</div>
                <strong className="text-primary">{claim.category}</strong>
              </Col>
              <Col sm={6} md={3}>
                <div className="text-muted small">CLAIMED AMOUNT</div>
                <h5 className="mb-0 text-success fw-bold">{formatMoney(claim.amount)}</h5>
              </Col>
            </Row>
          </div>

          {/* Detailed Fields */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="text-muted small">MERCHANT / VENDOR</div>
              <div className="fw-semibold">{claim.merchant || "Not specified"}</div>
            </Col>
            <Col md={6}>
              <div className="text-muted small">LINKED PROJECT / WORK ORDER</div>
              <div className="fw-semibold">
                {claim.project?.projectName || claim.project?.name || (claim.workOrder ? `${claim.workOrder.woNo || claim.workOrder.workOrderNumber || "Work Order"}${claim.workOrder.label ? ` – ${claim.workOrder.label}` : ""}` : "General Business Expense")}
              </div>
            </Col>
            <Col xs={12}>
              <div className="text-muted small">DESCRIPTION / PURPOSE</div>
              <div className="p-2 bg-light rounded border mt-1" style={{ fontSize: 13, minHeight: 48 }}>
                {claim.description || "No description provided."}
              </div>
            </Col>
          </Row>

          {/* Attached Receipts */}
          <div className="mb-3">
            <div className="text-muted small fw-semibold mb-2">ATTACHED RECEIPTS & BILLS ({claim.bills?.length || 0})</div>
            {(!claim.bills || claim.bills.length === 0) ? (
              <div className="p-3 text-center bg-light rounded text-muted small">
                No receipts attached with this claim
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {claim.bills.map((bill, idx) => {
                  const url = getFileUrl(bill.filePath);
                  const isImage = bill.mimeType?.startsWith("image/") || /\.(jpg|jpeg|png|webp|gif)$/i.test(bill.fileName);

                  return (
                    <div
                      key={idx}
                      className="p-2 border rounded bg-white shadow-sm d-flex flex-column align-items-center"
                      style={{ width: 130 }}
                    >
                      {isImage ? (
                        <div
                          style={{ width: "100%", height: 80, cursor: "pointer", overflow: "hidden", borderRadius: 4 }}
                          onClick={() => setPreviewImage(url)}
                          title="Click to zoom image"
                        >
                          <img
                            src={url}
                            alt={bill.fileName}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                      ) : (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="d-flex flex-column align-items-center justify-content-center bg-danger-subtle text-danger rounded"
                          style={{ width: "100%", height: 80, textDecoration: "none" }}
                          title="Open PDF"
                        >
                          <span style={{ fontSize: 12, fontWeight: "bold" }}>PDF DOCUMENT</span>
                          <span style={{ fontSize: 10 }}>VIEW</span>
                        </a>
                      )}
                      <div className="w-100 text-truncate text-center mt-1" style={{ fontSize: 11 }} title={bill.fileName}>
                        {bill.fileName}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Status Specific Details (Approved / Rejected / Settled) */}
          {claim.status === "APPROVED" && claim.approvalInfo && (
            <div className="p-3 mb-3 bg-info-subtle border border-info rounded">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong className="text-info-emphasis">Approved for {formatMoney(claim.approvedAmount || claim.amount)}</strong>
                  <div className="text-muted small">
                    Approved by {claim.approvalInfo.approvedBy?.name || "Manager"} on {claim.approvalInfo.approvedAt ? new Date(claim.approvalInfo.approvedAt).toLocaleDateString("en-IN") : ""}
                  </div>
                  {claim.approvalInfo.remarks && <div className="text-dark small mt-1">Note: {claim.approvalInfo.remarks}</div>}
                </div>
                <Badge bg="info">Awaiting Accounts Settlement</Badge>
              </div>
            </div>
          )}

          {claim.status === "REJECTED" && claim.rejectionInfo && (
            <div className="p-3 mb-3 bg-danger-subtle border border-danger rounded">
              <strong className="text-danger">Claim Rejected</strong>
              <div className="text-muted small">
                Rejected by {claim.rejectionInfo.rejectedBy?.name || "Manager"} on {claim.rejectionInfo.rejectedAt ? new Date(claim.rejectionInfo.rejectedAt).toLocaleDateString("en-IN") : ""}
              </div>
              <div className="text-danger small mt-1 fw-semibold">Reason: {claim.rejectionInfo.reason}</div>
            </div>
          )}

          {claim.status === "SETTLED" && claim.settlementInfo && (
            <div className="p-3 mb-3 bg-success-subtle border border-success rounded">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong className="text-success">Settled & Paid: {formatMoney(claim.approvedAmount || claim.amount)}</strong>
                  <div className="text-muted small">
                    Via <strong>{claim.settlementInfo.paymentMode}</strong> {claim.settlementInfo.paymentReference ? `(Ref: ${claim.settlementInfo.paymentReference})` : ""}
                    {claim.settlementInfo.settledBy?.name ? ` by ${claim.settlementInfo.settledBy.name}` : ""} on {claim.settlementInfo.settledAt ? new Date(claim.settlementInfo.settledAt).toLocaleDateString("en-IN") : ""}
                  </div>
                  {claim.settlementInfo.remarks && <div className="text-dark small mt-1">Remarks: {claim.settlementInfo.remarks}</div>}
                </div>
                <Badge bg="success">PAID</Badge>
              </div>
            </div>
          )}

          {/* MANAGER / HR REVIEW PANEL (Only if PENDING & user has review permission or Admin) */}
          {isReviewer && claim.status === "PENDING" && (
            <div className="p-3 rounded border border-warning bg-warning-subtle mb-3">
              <div className="fw-bold text-dark mb-2">Manager / HR Review & Verification</div>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Label className="small fw-semibold">Verified / Approved Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={approvedAmount}
                    onChange={(e) => setApprovedAmount(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label className="small fw-semibold">Review Remarks (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. Approved as per travel policy"
                    value={reviewRemarks}
                    onChange={(e) => setReviewRemarks(e.target.value)}
                  />
                </Col>
              </Row>

              {showRejectBox && (
                <div className="mt-3 p-2 bg-white rounded border border-danger">
                  <Form.Label className="small fw-semibold text-danger">Rejection Reason *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    placeholder="Provide specific reason for rejection..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-3">
                {showRejectBox ? (
                  <>
                    <Button variant="outline-secondary" size="sm" onClick={() => setShowRejectBox(false)}>
                      Cancel
                    </Button>
                    {canReject && (
                      <Button variant="danger" size="sm" onClick={handleReject} disabled={processing}>
                        {processing ? "Rejecting..." : "Confirm Rejection"}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {canReject && (
                      <Button variant="outline-danger" size="sm" onClick={() => setShowRejectBox(true)}>
                        Reject Claim
                      </Button>
                    )}
                    {canApprove && (
                      <Button variant="success" size="sm" onClick={handleApprove} disabled={processing}>
                        {processing ? "Approving..." : `Approve (₹${approvedAmount || claim.amount})`}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ACCOUNTS SETTLEMENT PANEL (Only if APPROVED & user has settle permission or Admin) */}
          {isSettler && claim.status === "APPROVED" && (
            <div className="p-3 rounded border border-success bg-success-subtle mb-3">
              <div className="fw-bold text-dark mb-2">Accounts & Finance Settlement</div>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Label className="small fw-semibold">Payment Mode *</Form.Label>
                  <Form.Select
                    size="sm"
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                  >
                    <option value="UPI">UPI Transfer</option>
                    <option value="Bank Transfer">NEFT / RTGS / IMPS</option>
                    <option value="Cash">Cash Payout</option>
                    <option value="Monthly Payroll">Add to Monthly Payroll</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="small fw-semibold">Transaction / UTR Reference</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. UTR / UPI Ref ID"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="small fw-semibold">Remarks (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. Paid via ICICI A/c"
                    value={settlementRemarks}
                    onChange={(e) => setSettlementRemarks(e.target.value)}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button variant="success" size="sm" onClick={handleSettle} disabled={processing}>
                  {processing ? <Spinner size="sm" /> : `Mark Settled & Paid (${formatMoney(claim.approvedAmount || claim.amount)})`}
                </Button>
              </div>
            </div>
          )}

          {/* Audit History Timeline */}
          {claim.auditHistory && claim.auditHistory.length > 0 && (
            <div className="mt-3">
              <div className="text-muted small fw-semibold mb-2">AUDIT TRAIL & ACTIVITY</div>
              <div className="p-2 bg-light rounded border" style={{ fontSize: 11 }}>
                {claim.auditHistory.map((item, idx) => (
                  <div key={idx} className="d-flex justify-content-between py-1 border-bottom border-light">
                    <div>
                      <span className="badge bg-secondary me-1">{item.status}</span>
                      <span>{item.note || `Status changed to ${item.status}`}</span>
                    </div>
                    <div className="text-muted">
                      {new Date(item.timestamp).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Zoom Image Modal */}
      {previewImage && (
        <Modal show={Boolean(previewImage)} onHide={() => setPreviewImage(null)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 15 }}>Receipt Full View</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-2">
            <img src={previewImage} alt="Receipt Preview" style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default ReimbursementDetailModal;
